import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Divider, Form, FormInstance, Input, Message, Select, Upload } from "@arco-design/web-react";
import classNames from 'classnames';

import { AVATAR_IMAGE_TYPES } from '../../../constants/imageTypes';
import { Role } from "../../../models/Person";
import { AvatarCropDialog } from "../../../components/AvatarCropDialog/AvatarCropDialog";
import { LanguagePills } from "../../../components/LanguagePills/LanguagePills";
import { RootState } from "../../../store/store";
import { fixUrl } from "../../../utils/fixUrl";
import { checkEmptyField, validateEmailField, validateUrlField, validateZipCode } from "../../../utils/validators/userFormValidators";
import { useTranslations } from "../../../utils/hooks/useTranslations";

import "./UserForm.css";
import { currentThemeName } from "../../../theme";
import { Language } from "../../../i18n.config";

type Props = {
  role: Role;
  form: FormInstance;
  avatarUrl?: string;
  isEditing?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
};

const TextArea = Input.TextArea;

export function UserForm({ form, role, avatarUrl: currentAvatarUrl, isEditing, onSubmit, onCancel }: Props) {
  const { t } = useTranslation();
  
  const isCalendlyLinkRequired = useSelector((state: RootState) => state.config.isCalendlyLinkRequired);
  const isAutoGoogleMeetEnabled = useSelector((state: RootState) => state.config.isAutoGoogleMeetEnabled);
  const isSurveyForm = useSelector((state: RootState) => state.config.isSurveyForm);
  const useNycIntake = useSelector((state: RootState) => state.config.useNycIntake);
  const isMenteeIntakeFieldRequired = isSurveyForm && useNycIntake;
  const calendlyFieldRequired = isCalendlyLinkRequired && !useNycIntake;
  const mwbeValue = Form.useWatch('mwbe', form);
  const showMwbeOther = useNycIntake && mwbeValue === 'Other';

  const [avatar, setAvatar] = useState<File | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState(currentAvatarUrl || form.getFieldValue("avatarUrl"));
  const [isFileTypeError, setIsFileTypeError] = useState(false);

  const { formLists, translateSelected } = useTranslations();

  const professionalExperienceOptions = useMemo(
    () => useNycIntake
      ? formLists.professionalExperienceOptions.filter((o) => o.value !== 'Other (please specify)')
      : formLists.professionalExperienceOptions,
    [useNycIntake, formLists.professionalExperienceOptions],
  );

  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedAdvisorCategories, setSelectedAdvisorCategories] = useState<string[]>([]);
  const [firstGoal, setFirstGoal] = useState<string>('');
  const [secondGoal, setSecondGoal] = useState<string>('');
  const [thirdGoal, setThirdGoal] = useState<string>('');

  const isBusinessFieldsAvailableInitial = form.getFieldValue('occupationStatus') === 'Entrepreneur (Business Owner/Self-Employed)';

  const [isBusinessFieldsAvailable, setIsBusinessFieldsAvailable] = useState(isBusinessFieldsAvailableInitial);

  const handleChangeOccupationStatus = (value: string) => {
    setIsBusinessFieldsAvailable(value === 'Entrepreneur (Business Owner/Self-Employed)')
  }

  const handleChangeIndustries = (value: string[]) => {
    if (value.length > 3) {
      Message.warning(t('notification_messages.industries_length_exceeded'));
      return;
    }
    setSelectedIndustries(value || []);
    form.setFieldsValue({ current_industry: value || [] });
  };

  const handleChangeGoals = (value: string[]) => {
    if (value.length > 3) {
      Message.warning(t('notification_messages.goals_length_exceeded')); 
      return;
    }
    setSelectedGoals(value || []);
  };

  const getIndustriesOptions = (options: {
    label: string;
    value: string;
  }[]) => {
    return options.map((option) => ({
      ...option,
      disabled:
        selectedIndustries.length >= 3 && !selectedIndustries.includes(option.value),
    }));
  };

  // Initialize selectedIndustries from form's current_industry field
  useEffect(() => {
    const currentIndustryValue = form.getFieldValue('current_industry');
    if (currentIndustryValue) {
      const currentIndustryArray = Array.isArray(currentIndustryValue) 
        ? currentIndustryValue 
        : (typeof currentIndustryValue === 'string' && currentIndustryValue.includes(';;;')
          ? currentIndustryValue.split(';;;').filter((s: string) => s.trim())
          : [currentIndustryValue]);
      setSelectedIndustries(currentIndustryArray);
    } else {
      setSelectedIndustries([]);
    }
  }, [form, isEditing]);

  useEffect(() => {
    if (role !== 'mentor' || !isEditing) return;
    const categories = form.getFieldValue('categories');
    setSelectedAdvisorCategories(Array.isArray(categories) ? categories : []);
  }, [form, isEditing, role]);

  useEffect(() => {
    if (!useNycIntake || !isEditing) return;

    const highGrowth = form.getFieldValue('highGrowthIndustries');
    if (Array.isArray(highGrowth) && highGrowth.length) {
      form.setFieldsValue({ highGrowthIndustries: highGrowth[0] });
    }

    const mwbe = form.getFieldValue('mwbe');
    if (typeof mwbe === 'string' && mwbe.startsWith('Other:')) {
      form.setFieldsValue({ mwbe: 'Other', mwbe_other: mwbe.slice(6).trim() });
    }

    if (role === 'mentor') {
      const industry = form.getFieldValue('current_industry');
      if (Array.isArray(industry) && industry.length) {
        form.setFieldsValue({ current_industry: industry[0] });
      }
    }

    if (role === 'mentee') {
      const industry = form.getFieldValue('current_industry');
      if (Array.isArray(industry) && industry.length) {
        form.setFieldsValue({ current_industry: industry[0] });
      } else if (typeof industry === 'string' && industry.includes(';;;')) {
        form.setFieldsValue({ current_industry: industry.split(';;;').filter((s: string) => s.trim())[0] });
      }
    }
  }, [useNycIntake, isEditing, form, role]);

  const getGoalsOptions = (options: {
    label: string;
    value: string;
  }[]) => {
    return options.map((option) => ({
      ...option,
      disabled:
        selectedGoals.length >= 3 && !selectedGoals.includes(option.value),
    }));
  };

  const handleChangeAdvisorCategories = (value: string[]) => {
    if (value.length > 3) {
      Message.warning(t('notification_messages.advisor_categories_length_exceeded'));
      return;
    }
    setSelectedAdvisorCategories(value || []);
    form.setFieldsValue({ categories: value || [] });
  };

  const getAdvisorCategoriesOptions = (options: {
    label: string;
    value: string;
  }[]) => {
    return options.map((option) => ({
      ...option,
      disabled:
        selectedAdvisorCategories.length >= 3 && !selectedAdvisorCategories.includes(option.value),
    }));
  };

  const getFirstGoalOptions = () => {
    return formLists.three_goals.filter(opt => opt.value !== secondGoal && opt.value !== thirdGoal);
  };

  const getSecondGoalOptions = () => {
    return formLists.three_goals.filter(opt => opt.value !== firstGoal && opt.value !== thirdGoal);
  };

  const getThirdGoalOptions = () => {
    return formLists.three_goals.filter(opt => opt.value !== firstGoal && opt.value !== secondGoal);
  };


  const prepareFormData = (formValues: any) => {
    const preparedData = { ...formValues };

    // Industries field stays as array - backend handles it correctly

    const fieldsToClean = [
      'borough', 'gender', 'age', 'race', 'ethnicity',
      'education', 'incomeRange', 'occupationStatus',
      'professionalExperience', 'howYouFoundUs',
      'highGrowthIndustries', 'business_start_period', 'mwbe', 'employees_count', 'business_stage', 'important_goal', 'business_location', 'revenue', 'item_types',
      'first_goal', 'second_goal', 'third_goal', 'long_term_goal', 'advisor_title', 'advisor_company', 'sub_industry', 'company_name', 'zip_code',
    ];

    fieldsToClean.forEach(field => {
      if (preparedData[field] === undefined || preparedData[field] === null) {
        preparedData[field] = "";
      }
    });

    if (useNycIntake) {
      if (typeof preparedData.highGrowthIndustries === 'string' && preparedData.highGrowthIndustries) {
        preparedData.highGrowthIndustries = [preparedData.highGrowthIndustries];
      }
      if (preparedData.mwbe === 'Other') {
        const otherText = preparedData.mwbe_other?.trim();
        if (otherText) {
          preparedData.mwbe = `Other: ${otherText}`;
        }
      }
      delete preparedData.mwbe_other;
    }

    return preparedData;
  };

  const handleSubmit = () => {
    if (avatar) form.setFieldsValue({ avatar_url: null });

    form.setFieldsValue({ file: avatar });
    const formValues = form.getFieldsValue();
    const preparedData = prepareFormData(formValues);
    form.setFieldsValue(preparedData);
    
    onSubmit();
  };

  const getFileList = () => {
    if (avatar) {
      return [{ originFile: avatar, uid: "1" }];
    }

    if (avatarUrl) {
      return [{
        uid: form.getFieldValue("id") || "avatar",
        url: currentAvatarUrl || form.getFieldValue("avatar_url"),
      }];
    }

    return undefined;
  }

  const onBeforeUpload = (file: File) => {
    if (!AVATAR_IMAGE_TYPES.includes(file.type)) {
      Message.warning(t('notification_messages.avatar_format_error'));
      setIsFileTypeError(true);
      return false;
    }

    setShowCropDialog(true);
    setImageFile(file);
    return false;
  }

  const renderAvatarUpload = () => (
    <Form.Item
      layout="vertical"
      label={t('profile.profile_picture_label')}
      triggerPropName="fileList"
      className="create-user-form__avatar-upload"
    >
      <Upload
        drag
        limit={1}
        autoUpload={false}
        imagePreview
        className={classNames('avatar-upload', {'avatar-upload__error': isFileTypeError})}
        defaultFileList={
          avatarUrl || currentAvatarUrl
            ? [
              {
                uid: form.getFieldValue("id") || "avatar",
                url: currentAvatarUrl || form.getFieldValue("avatar_url"),
              },
            ]
            : undefined
        }
        fileList={getFileList()}
        listType="picture-card"
        accept={{type: AVATAR_IMAGE_TYPES.join(), strict: false}}
        onChange={(fileList, file) => {
          setAvatar(file.originFile || null);
        }}
        onRemove={() => {
          setAvatar(null);
          setAvatarUrl("");
          form.setFieldsValue({ avatar_url: "" });
          return true;
        }}
        beforeUpload={onBeforeUpload}
      />
    </Form.Item>
  );

  return (
    <>
      {!!imageFile && (
        <AvatarCropDialog
          file={imageFile}
          visible={showCropDialog}
          onCancel={() => setShowCropDialog(false)}
          onConfirm={(file) => {
            setAvatar(file);
            setShowCropDialog(false);
          }}
        />
      )}
    <Form
      form={form}
      autoComplete="off"
      size="default"
      scrollToFirstError
      onSubmit={handleSubmit}
      onValuesChange={(changedValues, allValues) => {
        // Sync selectedIndustries when current_industry field changes
        if ('current_industry' in changedValues) {
          const currentIndustryValue = allValues.current_industry;
          if (currentIndustryValue) {
            const currentIndustryArray = Array.isArray(currentIndustryValue) 
              ? currentIndustryValue 
              : (typeof currentIndustryValue === 'string' && currentIndustryValue.includes(';;;')
                ? currentIndustryValue.split(';;;').filter((s: string) => s.trim())
                : [currentIndustryValue]);
            setSelectedIndustries(currentIndustryArray);
          } else {
            setSelectedIndustries([]);
          }
        }
      }}
      className={classNames("create-user-form", {
        "create-user-form--black-theme": currentThemeName === 'black'
      })}
    >
      {(role === 'mentor' || role === 'mentee') && (
      <>
      {renderAvatarUpload()}
       <Form.Item
          layout="vertical"
          label={t('profile.language_label')}
          field="language"
          initialValue={!isEditing ? Language.English : undefined}
        >
          <LanguagePills
            surface={!isEditing && currentThemeName === 'black' ? 'dark' : 'light'}
          />
        </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.name_label')}
        field="name"
          rules={[{ required: true, message: t('form_validation_messages.empty_user_name'), validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_user_name'))}]}
      >
        <Input
          placeholder={t('admin.user_form.name_placeholder')}
          autoComplete="off"
          className="create-user-form__input"
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.last_name_label')}
        field="last_name"
        rules={[{
          required: true,
          message: t('form_validation_messages.empty_last_name'),
          validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_last_name'))
        }]}
        >
        <Input
          placeholder={t('admin.user_form.last_name_placeholder')}
          autoComplete="off"
          className="create-user-form__input"
        />
      </Form.Item>
      {role === 'mentee' && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.company_name_label')}
          field="company_name"
        >
          <Input
            placeholder={t('admin.user_form.company_name_placeholder')}
            autoComplete="off"
            className="create-user-form__input"
          />
        </Form.Item>
      )}
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.email_label')}
        field="email"
        disabled={isEditing}
          rules={[{ required: true, message: t('form_validation_messages.empty_user_email')}, { validator: (v, cb) => validateEmailField(v, cb, t)}]}
      >
        <Input
          placeholder={t('admin.user_form.email_placeholder')}
          autoComplete="off"
          className="create-user-form__input"
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.linkedin_label')}
        field="linkedIn"
          rules={[{ required: false}, {
          validator: (value, callback) => validateUrlField(value, callback, t, false, 'linkedIn', 'linkedin.com')
        },]}
      >
        <Input
          placeholder={t('admin.user_form.linkedin_placeholder')}
          autoComplete="off"
          className="create-user-form__input"
          onBlur={() => {
            form.setFieldsValue({
              linkedIn: fixUrl(form.getFieldValue("linkedIn")),
            });
          }}
        />
      </Form.Item>
      {role === 'mentee' && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.zip_code_label')}
          field="zip_code"
          rules={[{
            required: isMenteeIntakeFieldRequired,
            validator: (v, cb) => validateZipCode(v, cb, t, isMenteeIntakeFieldRequired)
          }]}
        >
          <Input 
            type="text" 
            placeholder={t('admin.user_form.zip_code_placeholder')}
            maxLength={5}
          />
        </Form.Item>
      )}
      {role === 'mentee' && (
        <>
      {!useNycIntake && (
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.borough_label')}
            field="borough"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_borough'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_borough')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.borough_placeholder')}
              options={formLists.boroughOptions}
              allowClear
            />
          </Form.Item>
      )}
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.gender_label')}
            field="gender"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_gender'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_gender')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.gender_placeholder')}
              options={formLists.genderOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.age_label')}
            field="age"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_age'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_age')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.age_placeholder')}
              options={formLists.ageOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.race_label')}
            field="race"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_race'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_race')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.race_placeholder')}
              options={formLists.raceOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.ethnicity_label')}
            field="ethnicity"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_ethnicity'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_ethnicity')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.ethnicity_placeholder')}
              options={formLists.ethnicityOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.education_label')}
            field="education"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_education'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_education')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.education_placeholder')}
              options={formLists.educationOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.income_range_label')}
            field="incomeRange"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_income_range'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_income_range')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.income_range_placeholder')}
              options={formLists.incomeRangeOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.occupation_status_label')}
            field="occupationStatus"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_occupation_status'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_occupation_status')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.occupation_status_placeholder')}
              onChange={handleChangeOccupationStatus}
              options={formLists.occupationStatusOptions}
              allowClear
            />
          </Form.Item>
        </>
      )}
        {role === "mentee" && isSurveyForm && isBusinessFieldsAvailable && <Divider className="create-user-form__divider">
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.business_start_period_label')}
            field="business_start_period"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_business_start_period'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_business_start_period'))
            }]}
          >
            <Select
              placeholder={t('admin.user_form.business_start_period_placeholder')}
              options={formLists.business_start_period}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.mwbe_label')}
            field="mwbe"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_mwbe'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_mwbe'))
            }]}
          >
            <Select
              placeholder={t('admin.user_form.mwbe_placeholder')}
              options={formLists.mwbe}
              allowClear
              onChange={(value) => {
                if (value !== 'Other') {
                  form.setFieldsValue({ mwbe_other: undefined });
                }
              }}
            />
          </Form.Item>
          {showMwbeOther && (
            <Form.Item
              layout="vertical"
              label={t('admin.user_form.mwbe_other_label')}
              field="mwbe_other"
              rules={[{
                required: isMenteeIntakeFieldRequired,
                message: t('form_validation_messages.empty_mwbe_other'),
                validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_mwbe_other')) : cb()
              }]}
            >
              <Input
                placeholder={t('admin.user_form.mwbe_other_placeholder')}
                autoComplete="off"
              />
            </Form.Item>
          )}
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.employees_count_label')}
            field="employees_count"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_employees_count'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_employees_count'))
            }]}
          >
            <Select
              placeholder={t('admin.user_form.employees_count_placeholder')}
              options={formLists.employees_count}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.business_stage_label')}
            field="business_stage"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_business_stage'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_business_stage'))
            }]}
          >
            <Select
              placeholder={t('admin.user_form.business_stage_placeholder')}
              options={formLists.business_stage}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.business_location_label')}
            field="business_location"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_business_location'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_business_location'))
            }]}
          >
            <Select
              placeholder={t('admin.user_form.business_location_placeholder')}
              options={formLists.business_location}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.revenue_label')}
            field="revenue"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_revenue'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_revenue'))
            }]}
          >
            <Select
              placeholder={t('admin.user_form.revenue_placeholder')}
              options={formLists.revenue}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.items_type_label')}
            field="item_types"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_items_type'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_items_type'))
            }]}
          >
            <Select
              placeholder={t('admin.user_form.items_type_placeholder')}
              options={formLists.items_type}
              allowClear
            />
          </Form.Item>
        </Divider>}
        
      {role === "mentee" && isSurveyForm && (
        <>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.first_goal_label')}
            field="first_goal"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_first_goal'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_first_goal')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.first_goal_placeholder')}
              onChange={(value) => setFirstGoal(value)}
              options={getFirstGoalOptions()}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.second_goal_label')}
            field="second_goal"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_second_goal'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_second_goal')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.second_goal_placeholder')}
              onChange={(value) => setSecondGoal(value)}
              options={getSecondGoalOptions()}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.third_goal_label')}
            field="third_goal"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_third_goal'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_third_goal')) : cb()
            }]}
          >
            <Select
              placeholder={t('admin.user_form.third_goal_placeholder')}
              onChange={(value) => setThirdGoal(value)}
              options={getThirdGoalOptions()}
              allowClear
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.long_term_goal_label')}
            field="long_term_goal"
            rules={[{
              required: isMenteeIntakeFieldRequired,
              message: t('form_validation_messages.empty_long_term_goal'),
              validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_long_term_goal')) : cb()
            }]}
          >
            <Input
              placeholder={t('admin.user_form.long_term_goal_placeholder')}
              autoComplete="off"
            />
          </Form.Item>
        </>
      )}
      {role === 'mentee' && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.professional_experience_label')}
          field="professionalExperience"
          rules={[{
            required: isMenteeIntakeFieldRequired,
            message: t('form_validation_messages.empty_professional_experience'),
            validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_professional_experience')) : cb()
          }]}
        >
          <Select
            placeholder={t('admin.user_form.professional_experience_placeholder')}
            options={professionalExperienceOptions}
            allowClear
          />
        </Form.Item>
      )}
      {role === 'mentee' && useNycIntake && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.current_industry_label')}
          field="current_industry"
          rules={[{
            required: isMenteeIntakeFieldRequired,
            message: t('form_validation_messages.empty_industries'),
            validator: (v, cb) => {
              if (!isMenteeIntakeFieldRequired) return cb();
              if (!v) return cb(t('form_validation_messages.empty_industries'));
              cb();
            }
          }]}
        >
          <Select
            placeholder={t('admin.user_form.current_industry_placeholder')}
            options={formLists.industriesNycOptions}
            allowClear
            showSearch
          />
        </Form.Item>
      )}
      {role !== 'mentee' && (
        <>
          {/* advisor_title hidden from UI; value preserved for submit */}
          <Form.Item field="advisor_title" style={{ display: 'none', margin: 0 }}>
            <Input />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.advisor_company_label')}
            field="advisor_company"
          >
            <Input
              placeholder={t('admin.user_form.advisor_company_placeholder')}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.sub_industry_label')}
            field="sub_industry"
          >
            <Input
              placeholder={t('admin.user_form.sub_industry_placeholder')}
              autoComplete="off"
            />
          </Form.Item>
          {useNycIntake && (
            <Form.Item
              layout="vertical"
              label={t('admin.user_form.current_industry_label')}
              field="current_industry"
            >
              <Select
                placeholder={t('admin.user_form.current_industry_placeholder')}
                options={formLists.currentIndustryOptions}
                allowClear
              />
            </Form.Item>
          )}
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.proficiency_label')}
            field="categories"
          >
            <Select
              placeholder={t('admin.user_form.proficiency_placeholder')}
              mode="multiple"
              maxTagCount={3}
              value={selectedAdvisorCategories}
              onChange={handleChangeAdvisorCategories}
              options={getAdvisorCategoriesOptions(
                useNycIntake ? formLists.advisorCategoriesOptions : formLists.categoriesOptions
              )}
              allowClear
            />
          </Form.Item>
        </>
      )}
      {!useNycIntake && (
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.current_industry_label')}
        field="current_industry"
        rules={[{
          required: isMenteeIntakeFieldRequired,
          message: t('form_validation_messages.empty_industries'),
          validator: (v, cb) => {
            if (!isMenteeIntakeFieldRequired || role !== 'mentee') return cb();
            if (!v || v.length === 0) {
              return cb(t('form_validation_messages.empty_industries'));
            }
            cb();
          }
        }]}
        >
        <Select
          placeholder={t('admin.user_form.current_industry_placeholder')}
          mode="multiple"
          maxTagCount={3}
          value={selectedIndustries}
          onChange={handleChangeIndustries}
          options={getIndustriesOptions(formLists.industriesNycOptions)}
          allowClear
        />
      </Form.Item>
      )}
      {!isCalendlyLinkRequired && !isAutoGoogleMeetEnabled && role === "mentor" && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.booking_link_label')}
          field="bookingLink"
          rules={[{
            required: true,
            message: t('form_validation_messages.empty_user_booking_link'),
            validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_user_booking_link'))
          }]}
        >
          <Input
            placeholder={t('admin.user_form.booking_link_placeholder')}
          />
        </Form.Item>
      )}
      {isCalendlyLinkRequired && role === "mentor" && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.calendly_link_label')}
          field="calendlyLink"
          rules={[
            { required: calendlyFieldRequired, message: t('form_validation_messages.empty_user_calendly') }, {
              validator: (value, callback) => validateUrlField(value, callback, t, calendlyFieldRequired, 'Calendly', 'calendly.com')
            },]}
        >
          <Input
            placeholder={t('admin.user_form.calendly_link_placeholder')}
            onBlur={() => {
              form.setFieldsValue({
                calendlyLink: fixUrl(form.getFieldValue("calendlyLink")),
              });
            }}
          />
        </Form.Item>
      )}
      {role === "mentee" && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.high_industries_label')}
          field="highGrowthIndustries"
          rules={[{
            required: isMenteeIntakeFieldRequired,
            message: t('form_validation_messages.empty_high_growth_industries'),
            validator: (v, cb) => {
              if (!isMenteeIntakeFieldRequired || role !== 'mentee') return cb();
              if (useNycIntake) {
                if (!v) return cb(t('form_validation_messages.empty_high_growth_industries'));
              } else if (!v || v.length === 0) {
                return cb(t('form_validation_messages.empty_high_growth_industries'));
              }
              cb();
            }
          }]}
        >
          <Select
            placeholder={t('admin.user_form.high_industries_placeholder')}
            mode={useNycIntake ? undefined : "multiple"}
            options={formLists.highGrowthIndustryOptions}
            allowClear
          />
        </Form.Item>
      )}

      {role === "mentee" && (
        isSurveyForm ? <Form.Item
        layout="vertical"
        label={t('admin.user_form.important_goal_label')}
        field="important_goal"
        rules={[{
          required: isMenteeIntakeFieldRequired,
          message: t('form_validation_messages.empty_important_goal'),
          validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_important_goal')) : cb()
        }]}
        >
        <Select
          placeholder={t('admin.user_form.important_goal_placeholder')}
          options={formLists.important_goal}
          allowClear
        />
      </Form.Item> :
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.interests_label')}
          field="areaInterest"
        >
          <Select
            placeholder={t('admin.user_form.interests_placeholder')}
            mode="multiple"
            options={formLists.areaInterestOptions}
            allowClear
          />
        </Form.Item>
      )}

      {role === "mentee" && !isEditing && isSurveyForm && (
        <Form.Item
          layout="vertical"
          label={t('admin.user_form.source_label')}
          field="howYouFoundUs"
          rules={[{
            required: isMenteeIntakeFieldRequired,
            message: t('form_validation_messages.empty_how_you_found_us'),
            validator: (v, cb) => isMenteeIntakeFieldRequired ? checkEmptyField(v, cb, t('form_validation_messages.empty_how_you_found_us')) : cb()
          }]}
        >
          <Select
            placeholder={t('admin.user_form.source_placeholder')}
            options={formLists.howYouFoundUsOptions}
            allowClear
          />
        </Form.Item>
      )}

        <Form.Item layout="vertical" label={t('admin.user_form.website_label')} field="website">
        <Input
          type="text"
          placeholder={t('admin.user_form.website_placeholder')}
          onBlur={() => {
            form.setFieldsValue({
              website: fixUrl(form.getFieldValue("website")),
            });
          }}
        />
      </Form.Item>

      <Form.Item
        layout="vertical"
        label={
          useNycIntake && role === 'mentee'
            ? t('admin.user_form.tagline_label')
            : t('admin.user_form.short_description_label')
        }
        field="shortDescription"
        rules={[{
          required: isMenteeIntakeFieldRequired && role === 'mentee' && !useNycIntake,
          message: t('form_validation_messages.empty_short_description'),
          validator: (v, cb) => (
            isMenteeIntakeFieldRequired && role === 'mentee' && !useNycIntake
              ? checkEmptyField(v, cb, t('form_validation_messages.empty_short_description'))
              : cb()
          )
        }]}
        >
        <Input
          type="text"
          placeholder={
            useNycIntake && role === 'mentee'
              ? t('admin.user_form.tagline_placeholder')
              : t('admin.user_form.short_description_placeholder')
          }
          maxLength={150}
          showWordLimit
        />
      </Form.Item>

      <Form.Item
        layout="vertical"
        label={t('admin.user_form.bio_label')}
        field="fullBio"
        rules={[{
          required: useNycIntake ? false : isMenteeIntakeFieldRequired,
          message: t('form_validation_messages.empty_bio'),
          validator: (v, cb) => (useNycIntake || !isMenteeIntakeFieldRequired) ? cb() : checkEmptyField(v, cb, t('form_validation_messages.empty_bio'))
        }]}
        >
        <TextArea
          placeholder={t('admin.user_form.bio_placeholder')}
          autoSize={{ minRows: 6, maxRows: 16 }}
          maxLength={2600}
          showWordLimit
          style={{ whiteSpace: 'pre-line' }}
        />
      </Form.Item>

        <Form.Item layout="horizontal" wrapperCol={{ span: 24 }}>
          <div className="create-user-form__footer">
            <Button
              className="create-user-form__footer-button create-user-form__secondary-button"
              size="large"
              shape={"round"}
              onClick={() => {
                form.resetFields();
                onCancel();
              }}
            >
              {t('common.cancel_button')}
            </Button>
            <Button
              htmlType="submit"
              className={classNames("create-user-form__footer-button create-user-form__primary-button", {
                "create-user-form__primary-button--black-theme": currentThemeName === 'black'
              })}
              size="large"
              shape={"round"}
            >
              {t('common.save_button')}
            </Button>
          </div>
        </Form.Item>
      </>
      )}

      {role === 'guest' && (
      <>
      {renderAvatarUpload()}
      <Form.Item
        layout="vertical"
        label={t('profile.language_label')}
        field="language"
        initialValue={!isEditing ? Language.English : undefined}
      >
        <LanguagePills
          surface={!isEditing && currentThemeName === 'black' ? 'dark' : 'light'}
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.name_label')}
        field="name"
        rules={[{ required: true, message: t('form_validation_messages.empty_user_name'), validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_user_name'))}]}
      >
        <Input
          placeholder={t('admin.user_form.name_placeholder')}
          autoComplete="off"
          className="create-user-form__input"
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.last_name_label')}
        field="last_name"
        rules={[{
          required: true,
          message: t('form_validation_messages.empty_last_name'),
          validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_last_name'))
        }]}
      >
        <Input
          placeholder={t('admin.user_form.last_name_placeholder')}
          autoComplete="off"
          className="create-user-form__input"
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.email_label')}
        field="email"
        disabled={isEditing}
        rules={[{ required: true, message: t('form_validation_messages.empty_user_email')}, { validator: (v, cb) => validateEmailField(v, cb, t)}]}
      >
        <Input
          placeholder={t('admin.user_form.email_placeholder')}
          autoComplete="off"
          className="create-user-form__input"
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.short_description_label')}
        field="shortDescription"
      >
        <Input
          placeholder={t('admin.user_form.short_description_placeholder')}
          maxLength={150}
          showWordLimit
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('admin.user_form.bio_label')}
        field="fullBio"
      >
        <TextArea
          placeholder={t('admin.user_form.bio_placeholder')}
          autoSize={{ minRows: 6, maxRows: 16 }}
          maxLength={2600}
          showWordLimit
          style={{ whiteSpace: 'pre-line' }}
        />
      </Form.Item>
      <Form.Item
        layout="vertical"
        label={t('profile.location_label')}
        field="location"
      >
        <Input placeholder={t('profile.location_placeholder')} />
      </Form.Item>
      <Form.Item layout="vertical" label={t('admin.user_form.website_label')} field="website">
        <Input
          type="text"
          placeholder={t('admin.user_form.website_placeholder')}
          onBlur={() => {
            form.setFieldsValue({
              website: fixUrl(form.getFieldValue("website")),
            });
          }}
        />
      </Form.Item>
      <Form.Item layout="horizontal" wrapperCol={{ span: 24 }}>
        <div className="create-user-form__footer">
          <Button
            className="create-user-form__footer-button create-user-form__secondary-button"
            size="large"
            shape={"round"}
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
          >
            {t('common.cancel_button')}
          </Button>
          <Button
            htmlType="submit"
            className={classNames("create-user-form__footer-button create-user-form__primary-button", {
              "create-user-form__primary-button--black-theme": currentThemeName === 'black'
            })}
            size="large"
            shape={"round"}
          >
            {t('common.save_button')}
          </Button>
        </div>
      </Form.Item>
      </>
      )}

      {role === 'admin' && (
        <>
          {renderAvatarUpload()}
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.name_label')}
            field="name"
            rules={[{ required: true, message: t('form_validation_messages.empty_user_name'), validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_user_name'))}]}
          >
            <Input
              placeholder={t('admin.user_form.name_placeholder')}
              autoComplete="off"
              className="create-user-form__input"
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.last_name_label')}
            field="last_name"
            rules={[{
              required: true,
              message: t('form_validation_messages.empty_last_name'),
              validator: (v, cb) => checkEmptyField(v, cb, t('form_validation_messages.empty_last_name'))
            }]}
          >
            <Input
              placeholder={t('admin.user_form.last_name_placeholder')}
              autoComplete="off"
              className="create-user-form__input"
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.email_label')}
            field="email"
            disabled={isEditing}
            rules={[{ required: true, message: t('form_validation_messages.empty_user_email')}, { validator: (v, cb) => validateEmailField(v, cb, t)}]}
          >
            <Input
              placeholder={t('admin.user_form.email_placeholder')}
              autoComplete="off"
              className="create-user-form__input"
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.short_description_label')}
            field="shortDescription"
          >
            <Input
              placeholder={t('admin.user_form.short_description_placeholder')}
              maxLength={150}
              showWordLimit
              className="create-user-form__input"
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.linkedin_label')}
            field="linkedIn"
            rules={[{
              validator: (value, callback) => validateUrlField(value, callback, t, false, 'linkedIn', 'linkedin.com')
            }]}
          >
            <Input
              placeholder={t('admin.user_form.linkedin_placeholder')}
              autoComplete="off"
              className="create-user-form__input"
              onBlur={() => {
                form.setFieldsValue({
                  linkedIn: fixUrl(form.getFieldValue("linkedIn")),
                });
              }}
            />
          </Form.Item>
          <Form.Item
            layout="vertical"
            label={t('admin.user_form.website_label')}
            field="website"
          >
            <Input
              placeholder={t('admin.user_form.website_placeholder')}
              className="create-user-form__input"
              onBlur={() => {
                form.setFieldsValue({
                  website: fixUrl(form.getFieldValue("website")),
                });
              }}
            />
          </Form.Item>
          <Form.Item layout="horizontal" wrapperCol={{ span: 24 }}>
            <div className="create-user-form__footer">
              <Button
                className="create-user-form__footer-button create-user-form__secondary-button"
                size="large"
                shape={"round"}
                onClick={() => {
                  form.resetFields();
                  onCancel();
                }}
              >
                {t('common.cancel_button')}
              </Button>
              <Button
                htmlType="submit"
                className={classNames("create-user-form__footer-button create-user-form__primary-button", {
                  "create-user-form__primary-button--black-theme": currentThemeName === 'black'
                })}
                size="large"
                shape={"round"}
              >
                {t('common.save_button')}
              </Button>
            </div>
          </Form.Item>
        </>
      )}
      </Form>
    </>
  );
}